import {
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { ChangeEvent, useCallback, useEffect, useMemo } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  MasterClassFieldsFragment,
  useListClientsQuery,
  useListPatientCasesQuery,
} from '@/@generated/codegen/API';
import {
  CreateMasterClassFormData,
  masterClassInformationSchema,
} from '@/pages/admin/components/CreateMasterClassForm/createMasterClassFormSchema';
import { AppSpinner } from '@/ui/AppSpinner';
import ItemList from '@/ui/ItemList/ItemList';
import { useFormFieldArrayHelpers } from '@/utils/hooks/useFormFieldArrayHelpers';

type Props = {
  form: UseFormReturn<CreateMasterClassFormData>;
  masterClass?: MasterClassFieldsFragment;
  setFirstColumnValid: (value: boolean) => void;
  isDuplicating?: boolean;
};

export const CreateMasterClassFormColumnOne = ({
  form,
  masterClass,
  setFirstColumnValid,
  isDuplicating: isDuplicateMasterClass,
}: Props) => {
  const { t } = useTranslation();
  const {
    formState: { errors, isSubmitting },
  } = form;
  const patientCases = useListPatientCasesQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {
        isMasterClass: {
          equals: true,
        },
      },
    },
  });
  const patientCasesList = useMemo(() => {
    return patientCases.data?.listPatientCases.edges.map((edge) => edge.node);
  }, [patientCases.data]);

  const {
    handleAdd: handleAddPatientCase,
    handleRemove: handleRemovePatientCase,
    getAvailableItems: getAvailablePatientCases,
  } = useFormFieldArrayHelpers({
    form,
    arrayFieldName: 'patientCaseIDs',
    selectorFieldName: 'selectedPatientCaseID',
    allElements: patientCasesList ?? [],
  });

  const clients = useListClientsQuery({
    variables: {
      paginationArgs: {
        first: 100,
      },
      where: {},
    },
  });
  const clientsList = useMemo(() => {
    return clients.data?.listClients.edges.map((edge) => edge.node);
  }, [clients.data]);

  const {
    handleAdd: handleAddClient,
    handleRemove: handleRemoveClient,
    getAvailableItems: getAvailableClients,
  } = useFormFieldArrayHelpers({
    form,
    arrayFieldName: 'clientIDs',
    selectorFieldName: 'selectedClientID',
    allElements: clientsList ?? [],
  });

  const currentTitle = form.watch('title');
  const currentMasterClassClientIDs = form.watch('clientIDs') ?? [];
  const currentMasterClassPatientCaseIDs = form.watch('patientCaseIDs') ?? [];

  const getClientNameById = useCallback(
    (id: string) => {
      if (!clientsList) return null;
      const client = clientsList.find((client) => client.id === id);
      return client ? client.name : null;
    },
    [clientsList]
  );

  const getPatientCaseNameById = useCallback(
    (id: string) => {
      if (!patientCasesList) return null;
      const patientCase = patientCasesList.find((patientCase) => patientCase.id === id);
      return patientCase ? patientCase.name : null;
    },
    [patientCasesList]
  );

  useEffect(() => {
    const result = masterClassInformationSchema.safeParse({
      name: form.getValues('title'),
      clientIDs: form.getValues('clientIDs'),
      patientCaseIDs: form.getValues('patientCaseIDs'),
    });

    setFirstColumnValid(result.success);
  }, [currentTitle, currentMasterClassClientIDs, currentMasterClassPatientCaseIDs]);

  const onClientChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      form.setValue('selectedClientID', e.target.value);
      handleAddClient();
      form.setValue('selectedClientID', '');
    },
    [form, handleAddClient]
  );

  const onPatientCaseChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      form.setValue('selectedPatientCaseID', e.target.value);
      handleAddPatientCase();
      form.setValue('selectedPatientCaseID', '');
    },
    [form, handleAddPatientCase]
  );

  if (!clients.data || !patientCases.data) {
    return (
      <Flex w="full" h="full" justifyContent="center" alignItems="center">
        <AppSpinner />
      </Flex>
    );
  }

  return (
    <>
      <FormControl isRequired isInvalid={Boolean(errors.title)}>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMasterClass.form.masterClassName.label')}
        </FormLabel>
        <Input
          type="text"
          aria-label="Choose a masterClass name"
          {...form.register('title', { required: true })}
          placeholder="The perfect masterClass"
        />
        {errors.title && (
          <FormHelperText color="red">
            {t('admin.createMasterClass.form.masterClassName.error')}
          </FormHelperText>
        )}
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMasterClass.form.masterClassClients.label')}
        </FormLabel>
        <Controller
          name="selectedClientID"
          control={form.control}
          render={({ field }) => (
            <Select
              placeholder={
                t('admin.createMasterClass.form.masterClassClients.placeholder') ||
                'Click to add a client'
              }
              aria-label="Add a client to the masterClass"
              {...field}
              onChange={onClientChange}
              required={form.getValues('clientIDs').length === 0}
              value={field.value ?? undefined}
            >
              {getAvailableClients().map((elem) => (
                <option key={elem.id} value={elem.id}>
                  {elem.name}
                </option>
              ))}
            </Select>
          )}
        />
        <VStack p={2} align="flex-start">
          {currentMasterClassClientIDs.length && (
            <ItemList
              itemList={currentMasterClassClientIDs.map((id) => ({
                id: id,
                value: getClientNameById(id)!,
              }))}
              removeItemFunc={handleRemoveClient}
            />
          )}
          {currentMasterClassClientIDs.length === 0 && (
            <Text>{t('admin.createMasterClass.form.masterClassClients.empty')}</Text>
          )}
          {errors.clientIDs && (
            <FormHelperText color="red">{errors.clientIDs.message}</FormHelperText>
          )}
        </VStack>
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm" fontWeight="normal" mb="1">
          {t('admin.createMasterClass.form.masterClassPatientCases.label')}
        </FormLabel>
        <Controller
          name="selectedPatientCaseID"
          control={form.control}
          render={({ field }) => (
            <Select
              placeholder={
                t('admin.createMasterClass.form.masterClassPatientCases.placeholder') ||
                'Click to add a patient case'
              }
              aria-label="Add a patient case to the masterClass"
              {...field}
              onChange={onPatientCaseChange}
              required={form.getValues('patientCaseIDs').length === 0}
              value={field.value ?? undefined}
            >
              {getAvailablePatientCases().map((elem) => (
                <option key={elem.id} value={elem.id}>
                  {elem.name}
                </option>
              ))}
            </Select>
          )}
        />
        <VStack p={2} align="flex-start">
          {currentMasterClassPatientCaseIDs.length && (
            <ItemList
              itemList={currentMasterClassPatientCaseIDs.map((id) => ({
                id: id,
                value: getPatientCaseNameById(id)!,
              }))}
              removeItemFunc={handleRemovePatientCase}
            />
          )}
          {currentMasterClassPatientCaseIDs.length === 0 && (
            <Text>{t('admin.createMasterClass.form.masterClassPatientCases.empty')}</Text>
          )}
          {errors.patientCaseIDs && (
            <FormHelperText color="red">{errors.patientCaseIDs.message}</FormHelperText>
          )}
        </VStack>
      </FormControl>
    </>
  );
};
